<template>
    <v-data-table
            class="elevation-0"
            :headers="headers"
            :items="items"
            :items-per-page="15"
            :footer-props="dataTableFooterProps"
            :search="dataTableSearch"
            :custom-filter="dataTableCustomFilter"
            :loading="isBusy"
            :loading-text="$t('common.loading_table_data')"
            item-key="index"
            show-expand
            :expanded.sync="expanded"
    >
        <template v-slot:top>
            <v-toolbar flat color="white">
                <v-text-field
                        v-model="dataTableSearch"
                        prepend-inner-icon="mdi-magnify"
                        :placeholder="$t('common.filter')"
                        outlined
                        dense
                        hide-details
                />
            </v-toolbar>
        </template>
        <template v-slot:item.index="{ item }">
            {{ item.index }}
        </template>
        <template v-slot:item.recipients_count="{ item }">
            {{ item.recipients_count }}
        </template>
        <template v-slot:item.products_count="{ item }">
            {{ item.products_count }}
        </template>
        <template v-slot:item.message="{ item }">
            {{ item.message ? item.message : '-' }}
        </template>
        <template v-slot:item.actions="{ item }" v-if="!readonly">
            <span class="text-no-wrap">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn icon @click="onBackToShoppingCart(item)" v-on="on" color="secondary">
                            <v-icon>
                                mdi-cart-arrow-up
                            </v-icon>
                        </v-btn>
                    </template>
                    <span>{{ $t('tooltip.bulk_order.back_to_shopping_cart') }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn icon @click="onDelete(item)" v-on="on" color="error">
                            <v-icon>
                                mdi-trash-can
                            </v-icon>
                        </v-btn>
                    </template>
                    <span>{{ $t('tooltip.bulk_order.delete') }}</span>
                </v-tooltip>
            </span>
        </template>
        <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-3">
                <v-card flat>
                    <v-card-subtitle class="pb-0">
                        {{ $t('bulk_orders.products') }}
                    </v-card-subtitle>
                    <v-card-text>
                        <v-simple-table>
                            <template v-slot:default>
                                <thead>
                                <tr>
                                    <th>{{ $t('bulk_orders.preview') }}</th>
                                    <th>{{ $t('bulk_orders.name') }}</th>
                                    <th>{{ $t('bulk_orders.model') }}</th>
                                    <th>{{ $t('bulk_orders.quantity') }}</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="(product, i) in item.order" :key="`product-` + i">
                                    <td>
                                        <v-avatar size="50" tile>
                                            <v-img :src="product.image_url" contain/>
                                        </v-avatar>
                                    </td>
                                    <td>{{ product.name }}</td>
                                    <td>{{ product.model }}</td>
                                    <td>{{ product.quantity }} {{ $t('products.pieces') }}</td>
                                </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-card-text>
                    <v-divider/>
                    <v-card-subtitle class="pb-0">
                        {{ $t('bulk_orders.recipients') }}
                    </v-card-subtitle>
                    <v-card-text>
                        <v-simple-table>
                            <template v-slot:default>
                                <thead>
                                <tr>
                                    <th>{{ $t('bulk_orders.recipient') }}</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="(recipient, i) in item.recipients" :key="`recipient-` + i">
                                    <td>{{ recipient.address_string }}</td>
                                </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-card-text>
                </v-card>
            </td>
        </template>
    </v-data-table>
</template>

<script>
import BulkOrderMixin from '@/mixins/BulkOrderMixin'
import ShoppingCartMixin from '@/mixins/ShoppingCartMixin'
import {EventBus} from "@/eventBus";
import SnackbarMixin from "@/mixins/SnackbarMixin";
import DataTableMixin from "@/mixins/DataTableMixin";

export default {
    name: "BulkOrdersTable",
    mixins: [BulkOrderMixin, SnackbarMixin, ShoppingCartMixin, DataTableMixin],
    props: {
        readonly: {
            type: Boolean,
            default: () => false
        }
    },
    data: () => ({
        // expanded rows
        expanded: []
    }),
    computed: {
        isBusy () {
            return false;
        },
        headers () {
            return [
                {
                    text: '#',
                    value: 'index',
                    sortable: false
                },
                {
                    text: this.$t('bulk_orders.products'),
                    value: 'products_count'
                },
                {
                    text: this.$t('bulk_orders.recipients'),
                    value: 'recipients_count'
                },
                {
                    text: this.$t('bulk_orders.message'),
                    value: 'message'
                },
                {
                    text: '',
                    value: 'actions',
                    align: 'end',
                    sortable: false
                }
            ];
        },
        items () {
            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            let warehouseId = this.$store.getters['warehouse/getWarehouseId'](userId);
            if (warehouseId === null) {
                this.snackbarError(this.$t('snackbar.warehouses.not_selected'));
                return;
            }

            let index = 1;
            return this.getBulkOrders(userId, warehouseId)
                .map(row => {
                    row.index = index++;
                    row.products_count = row.order.length;
                    row.recipients_count = row.recipients.length;
                    row.__search = [
                        row.index,
                        row.products_count,
                        row.recipients_count,
                        row.message,
                        row.order.map(product => [product.name, product.model, product.quantity]),
                        row.recipients.map(recipient => recipient.address_string)
                    ].map(value => this.normalizeDataTableText(value)).join(" ");
                    return row;
                });
        }
    },
    methods: {
        onDelete (item, withConfirmation = true) {
            if (withConfirmation) {
                if (!confirm(this.$t('confirm.delete_bulk_order_shopping_cart'))) {
                    return;
                }
            }

            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            let warehouseId = this.$store.getters['warehouse/getWarehouseId'](userId);
            if (warehouseId === null) {
                this.snackbarError(this.$t('snackbar.warehouses.not_selected'));
                return;
            }

            this.removeOrderFromBulkOrders(userId, warehouseId, item);

            EventBus.$emit('refresh-shopping-cart-indicator');
        },
        onBackToShoppingCart (item) {
            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            let warehouseId = this.$store.getters['warehouse/getWarehouseId'](userId);
            if (warehouseId === null) {
                this.snackbarError(this.$t('snackbar.warehouses.not_selected'));
                return;
            }

            item.order.forEach(product => {
                this.addProductToShoppingCart(userId, warehouseId, product, product.quantity)
            });

            item.recipients.forEach(recipient => {
                this.addRecipientToShoppingCart(userId, warehouseId, recipient);
            });

            this.onDelete(item, false);
            this.$router.push({ name: 'shopping-cart' });
        }
    }
}
</script>
